import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchEquipmentIOTData, setPendingToStartSurvey } from '../../application_actions.js';
import { InfoBox } from '../presentational/info_box.jsx';
import { Navigator } from '../presentational/navigator.jsx';
import { LoadingCircular } from '../presentational/loading.jsx';
import { startIncompleteSurvey, fetchSurveyData } from '../../application_actions.js';
import { ERR_NETWORK, SERVER_TIMED_OUT } from '../../helpers/constants.js';
import makeBasePage from '../base_page.jsx';

const EquipmentValidationComplete = ({ strings, goForward, goBack }) => {
  const dispatch = useDispatch();

  const app = useSelector(state => state.app);
  const {
    KEN,
    SO,
    iotData,
    iotDataError,
    iotDataLoading,
    internetStatus,
    pendingToStartSurvey,
  } = app;

  useEffect(() => {
    if (!iotData || (iotData && iotData.equipment.EquipmentNumberKENformatted !== KEN)) {
      dispatch(fetchEquipmentIOTData(KEN));
    }
  }, [dispatch, iotData, KEN]);

  const getContent = () => {
    if (iotDataLoading || (!iotData && !iotDataError)) {
      return <LoadingCircular />;
    }

    let error;
    const data = [
      { name: strings.KoneEquipment.KEN, value: KEN }
    ];

    if (iotDataError) {
      switch (iotDataError) {
        case 404:
          error = strings.EquipmentValidationComplete.equipmentNotFoundError;
          break;
        case 502:
          error = SERVER_TIMED_OUT;
          break;
        default:
          error = strings.EquipmentValidationComplete.locationServiceConnectionError;
          break;
      }
    } else {
      const serviceOrderString = strings.KoneEquipment.serviceOrder;
      const location = iotData.equipment;
      const locationStr =
        `${location.EquipmentAddressPostalCode ? location.EquipmentAddressPostalCode + ', ' : ''}` +
        `${location.EquipmentAddressName1 ? location.EquipmentAddressName1 + ', ' : ''}` +
        `${location.EquipmentAddressCity1 || ''}`;
      const otherInfo = location.OtherInfo;

      data.push(
        { name: serviceOrderString || 'Service Order', value: SO },
        { name: strings.KoneEquipment.location, value: locationStr },
        { name: strings.KoneEquipment.otherInfo, value: otherInfo }
      );
    }

    return (
      <InfoBox
        key={KEN}
        data={data}
        error={error}
        errorName={error === SERVER_TIMED_OUT
          ? strings.EquipmentValidationComplete.unableToFetchEquipment
          : null}
      />
    );
  };

  const confirmLocation = useCallback(async () => {
    if (internetStatus && pendingToStartSurvey === undefined) {
      try {
        await dispatch(fetchSurveyData(KEN));
      } catch (e) {
        console.log('Error in fetching site survey data', e);
      }

      try {
        await startIncompleteSurvey(
          KEN,
          iotData.equipment.EquipmentCountryCode,
          SO
        );
        dispatch(setPendingToStartSurvey(false));
      } catch (e) {
        if (e.message === ERR_NETWORK) {
          dispatch(setPendingToStartSurvey(true));
        }
      }
    } else {
      dispatch(setPendingToStartSurvey(true));
    }

    goForward();
  }, [internetStatus, pendingToStartSurvey, dispatch, KEN, SO, iotData, goForward]);

  const titleText = strings.EquipmentValidationComplete.validationSuccess;
  const titleClass = 'ok';

  return (
    <div className="page-view" id="validation-complete">
      <h1 className={titleClass}>{titleText}</h1>
      {getContent()}
      {(iotData || iotDataError) && (
        <Navigator
          continueText={strings.Common.confirm}
          onContinue={confirmLocation}
          onBack={goBack}
          backText={strings.Common.back}
          continueDisabled={!!iotDataError || !iotData}
        />
      )}
    </div>
  );
};

export default makeBasePage('EquipmentValidationComplete')(EquipmentValidationComplete);
